import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@sakhtar/design-system/react';
import { YarMark } from '../../../shell/yar/YarMark.js';
import { faInt } from './format.js';

export interface AssistantMenuProps {
  /** Tenant base path, e.g. `/t/kimia`. */
  readonly base: string;
  readonly isTenantAdmin: boolean;
  /** The assistant has not been switched on / completed for this tenant. */
  readonly needsSetup: boolean;
  readonly balance: number;
  readonly balanceLabel?: string | undefined;
}

/**
 * The assistant's own menu as a bubble at the foot of the history (the way
 * ChatGPT / Claude keep account and settings): knowledge base, token wallet and
 * — for tenant admins — the assistant's settings. A pulsing red dot says the
 * tenant has not been set up yet and where to finish it.
 */
export function AssistantMenu({
  base,
  isTenantAdmin,
  needsSetup,
  balance,
  balanceLabel,
}: AssistantMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const items = [
    { href: `${base}/ai/documents`, label: 'دانش و اسناد سازمانی', icon: 'layers' as const },
    { href: `${base}/ai/wallet`, label: 'کیف پول توکن', icon: 'star' as const },
    ...(isTenantAdmin
      ? [
          {
            href: `${base}/ai/settings`,
            label: 'تنظیمات دستیار',
            icon: 'settings' as const,
            attention: needsSetup,
          },
        ]
      : []),
  ];

  return (
    <div className="ai-menu" ref={rootRef}>
      {open ? (
        <div className="ai-menu__pop" role="menu" aria-label="منوی دستیار">
          {needsSetup ? (
            <p className="ai-menu__setup" role="status">
              {isTenantAdmin
                ? 'دستیار برای این سازمان هنوز فعال نشده است. تنظیمات را کامل کنید.'
                : 'دستیار هنوز برای سازمان فعال نشده است؛ فعال‌سازی با مدیر سازمان است.'}
            </p>
          ) : null}
          {items.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              role="menuitem"
              className="ai-menu__item"
              onClick={() => setOpen(false)}
            >
              <Icon name={item.icon} size="sm" />
              <span>{item.label}</span>
              {'attention' in item && item.attention ? (
                <span className="ai-pulse" aria-label="نیازمند تکمیل" />
              ) : null}
            </Link>
          ))}
        </div>
      ) : null}
      <button
        type="button"
        className="ai-menu__bubble"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="ai-menu__mark" aria-hidden="true">
          <YarMark size={30} />
          {needsSetup ? <span className="ai-pulse ai-pulse--corner" /> : null}
        </span>
        <span className="ai-menu__text">
          <b>دستیار هوشمند</b>
          <small>
            {balanceLabel ?? 'موجودی'} <span className="sk-num">{faInt(balance)}</span> توکن
          </small>
        </span>
        <Icon name="chevronDown" size="sm" />
      </button>
    </div>
  );
}
